import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Layout } from '../../../../../components/auth';
import { Button, Input } from '../../../../../components/common';
import { FetchStatus } from '../../../../../enums';
import { useAuth, useCheckEmailInDatabase, useResetCheckEmailInDatabaseStatus } from '../../../../../features/auth';
import { isEmailValid } from '../../../../../utils';
import { ISignUpEmailProps } from './types';

export const SignUpEmail = ({ onComplete }: ISignUpEmailProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const checkEmailInDatabase = useCheckEmailInDatabase();
  const resetCheckEmailInDatabaseStatus = useResetCheckEmailInDatabaseStatus();
  const {
    status: { checkEmailInDatabase: checkEmailInDatabaseStatus },
  } = useAuth();

  const isValid = useMemo(() => isEmailValid(email), [email]);

  const check = useCallback(() => {
    checkEmailInDatabase(email);
  }, [email, checkEmailInDatabase]);

  useEffect(() => {
    resetCheckEmailInDatabaseStatus();
  }, []);

  useEffect(() => {
    if (checkEmailInDatabaseStatus === FetchStatus.SUCCESS) {
      resetCheckEmailInDatabaseStatus();
      onComplete(email);
    }
  }, [checkEmailInDatabaseStatus]);

  return (
    <Layout title={t('signup.title.email')}>
      <p className="typography-paragraph-3 text-text-secondary mt-2 mb-6">
        <Trans
          i18nKey="signup.subtitle.email"
          components={[
            <Link key="link" to="/login" className="typography-inline-link-3 text-text-link cursor-pointer" />,
          ]}
        />
      </p>

      <Input
        value={email}
        label={t('signup.field.email.label')}
        onChange={setEmail}
        placeholder={t('signup.field.email.placeholder')}
        type="email"
        hasError={checkEmailInDatabaseStatus === FetchStatus.FAILURE}
        errorLabel={t('signup.validation.email')}
      />

      <div className="flex mt-6">
        <Button
          loading={checkEmailInDatabaseStatus === FetchStatus.LOADING}
          text={t('signup.button.next')}
          onClick={check}
          disabled={!isValid}
        />
      </div>
    </Layout>
  );
};
